import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Topbar.css';

import API_URL from '../../config';

const NAV_TABS = [
  { id: 'akis',        label: 'Anasayfa',    path: '/dashboard' },
  { id: 'kesf',        label: 'Keşfet',      path: '/explore' },
  { id: 'topluluklar', label: 'Topluluklar', path: '/groups' },
  { id: 'agim',        label: 'A\u011f\u0131m',        path: '/feed' },
];

// Dropdown sekmeleri — alt öğeler admin panelinden (nav config) gelir.
const MENU_TABS = [
  { id: 'haberler',    label: 'Haberler',     menuKey: 'haberler' },
  { id: 'sorular',     label: 'Sorular',      menuKey: 'sorular' },
  { id: 'daha',        label: 'Daha Fazlası', menuKey: 'daha_fazlasi' },
];

// Lokalde hızlı yanıt için fallback
const DEFAULT_MENUS = {
  haberler:     [{ label: 'Tüm Haberler', path: '/news' }],
  sorular:      [{ label: 'Tüm Sorular', path: '/questions' }],
  daha_fazlasi: [
    { label: 'Mevzuat',      path: '/regulations' },
    { label: 'Uyarılar',     path: '/warnings' },
    { label: 'İncelemeler',  path: '/reviews' },
    { label: 'Anketler',     path: '/polls' },
    { label: 'Dokümanlar',   path: '/documents' },
    { label: 'İş İlanları',  path: '/jobs' },
    { label: 'Şablonlar',    path: '/templates' },
  ],
};

const Topbar = ({ user, onLogout, searchValue = '', onSearchChange, searchPlaceholder }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [openMenuKey, setOpenMenuKey] = useState(null); // hangi dropdown sekmesi açık
  const [navMenus, setNavMenus] = useState(DEFAULT_MENUS);
  const menuRef = useRef(null);
  const editorRef = useRef(null);
  const navMenuRef = useRef(null);

  // Admin tarafından yönetilen navigasyon konfigürasyonunu çek
  useEffect(() => {
    fetch(`${API_URL}/api/system-settings/navigation`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const cfg = d?.navigation?.topbarMenus;
        if (cfg) setNavMenus({
          haberler:     cfg.haberler     || DEFAULT_MENUS.haberler,
          sorular:      cfg.sorular      || DEFAULT_MENUS.sorular,
          daha_fazlasi: cfg.daha_fazlasi || DEFAULT_MENUS.daha_fazlasi,
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (editorRef.current && !editorRef.current.contains(e.target)) setEditorOpen(false);
      if (navMenuRef.current && !navMenuRef.current.contains(e.target)) setOpenMenuKey(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  const initials = (user.firstName?.[0] || '') + (user.lastName?.[0] || '');

  return (
    <header className="app-topbar">
      <div className="app-topbar-left">
        <div className="app-topbar-brand" onClick={() => navigate('/dashboard')}>
          <img src="/favicon.svg" alt="P" className="app-topbar-brand-icon" />
          <span className="app-topbar-brand-text">Pro<span>jabze</span><span className="beta-badge">beta</span></span>
        </div>
      </div>

      {/* Navigation tabs — sabit linkler + admin-tanımlı dropdown menüler */}
      <nav className="app-topbar-nav" ref={navMenuRef}>
        {NAV_TABS.map(tab => (
          <button
            key={tab.id}
            className={`app-topbar-tab ${location.pathname === tab.path ? 'active' : ''}`}
            onClick={() => navigate(tab.path)}
          >
            {tab.label}
          </button>
        ))}
        {MENU_TABS.map(tab => {
          const items = navMenus[tab.menuKey] || [];
          if (items.length === 0) return null;
          const isActiveTab = items.some(it => it.path === location.pathname);
          const isOpen = openMenuKey === tab.menuKey;
          return (
            <div key={tab.id} className="app-topbar-tab-menu">
              <button
                className={`app-topbar-tab ${isActiveTab ? 'active' : ''} ${isOpen ? 'menu-open' : ''}`}
                onClick={() => setOpenMenuKey(isOpen ? null : tab.menuKey)}
              >
                {tab.label}
                <svg className={`app-topbar-tab-chevron ${isOpen ? 'open' : ''}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {isOpen && (
                <div className="app-topbar-tab-dropdown">
                  {items.map((it, i) => (
                    <button
                      key={i}
                      className={`app-topbar-tab-dropdown-item ${location.pathname === it.path ? 'active' : ''}`}
                      onClick={() => { setOpenMenuKey(null); navigate(it.path); }}
                    >
                      {it.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="app-topbar-center">
        <div className="app-topbar-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder={searchPlaceholder || 'Ara...'}
            className="app-topbar-search-input"
            value={searchValue}
            onChange={e => onSearchChange?.(e.target.value)}
          />
          {searchValue && (
            <button className="app-topbar-search-clear" onClick={() => onSearchChange?.('')}>×</button>
          )}
        </div>

        {/* Editor selector */}
        <div className="app-topbar-editor-wrapper" ref={editorRef}>
          <button className="app-topbar-editor-btn" onClick={() => setEditorOpen(prev => !prev)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/>
              <circle cx="11" cy="11" r="2"/>
            </svg>
            <span>Editorum</span>
            <svg className={`app-topbar-editor-chevron ${editorOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          {editorOpen && (
            <div className="app-topbar-editor-dropdown">
              <button className="app-topbar-editor-item" onClick={() => { setEditorOpen(false); window.open('/app?editor=mesken-tek', 'projabze-editor'); }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                <span>Mesken Tek Sayac</span>
              </button>
              <button className="app-topbar-editor-item" onClick={() => { setEditorOpen(false); window.open('/app?editor=mesken-tek-cok-kat', 'projabze-editor'); }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="4" y1="8" x2="20" y2="8"/><line x1="4" y1="14" x2="20" y2="14"/>
                </svg>
                <span>Mesken Tek Sayac Cok Kat</span>
              </button>
              <button className="app-topbar-editor-item" onClick={() => { setEditorOpen(false); window.open('/app?editor=mesken-cok-sayac', 'projabze-editor'); }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="18" rx="2"/><line x1="12" y1="3" x2="12" y2="21"/><line x1="2" y1="12" x2="22" y2="12"/>
                </svg>
                <span>Mesken Cok Sayac</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="app-topbar-right">
        {/* Notifications */}
        <button className="app-topbar-icon-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
        </button>

        {/* User avatar + dropdown */}
        <div className="app-topbar-user-wrapper" ref={menuRef}>
          <div className="app-topbar-user" onClick={() => setMenuOpen(prev => !prev)}>
            <div className="app-topbar-avatar">
              {user.avatar ? <img src={API_URL + user.avatar} alt="" /> : initials}
            </div>
            <span className="app-topbar-username">{user.firstName}</span>
            <svg className={`app-topbar-chevron ${menuOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>

          {menuOpen && (
            <div className="app-topbar-dropdown">
              <div className="app-topbar-dropdown-header">
                <div className="app-topbar-dropdown-avatar">
                  {user.avatar ? <img src={API_URL + user.avatar} alt="" /> : initials}
                </div>
                <div className="app-topbar-dropdown-info">
                  <span className="app-topbar-dropdown-name">{user.firstName} {user.lastName}</span>
                  <span className="app-topbar-dropdown-email">{user.email}</span>
                </div>
              </div>
              <div className="app-topbar-dropdown-divider" />
              <button className="app-topbar-dropdown-item" onClick={() => { setMenuOpen(false); navigate(`/profile/${user.id}`); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                <span>Profil</span>
              </button>
              <button className="app-topbar-dropdown-item" onClick={() => { setMenuOpen(false); navigate('/settings'); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                <span>Ayarlar</span>
              </button>
              <button className="app-topbar-dropdown-item" onClick={() => { setMenuOpen(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span>Yardim</span>
              </button>
              {user.role === 'admin' && (
                <button className="app-topbar-dropdown-item admin" onClick={() => { setMenuOpen(false); navigate('/admin'); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  <span>Admin Paneli</span>
                </button>
              )}
              <div className="app-topbar-dropdown-divider" />
              <button className="app-topbar-dropdown-item logout" onClick={() => { setMenuOpen(false); onLogout?.(); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                <span>Cikis Yap</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
